import React, { useState, useEffect, useCallback, useMemo } from "react";
import clsx from "clsx";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import {
  getMobileViewportHeight,
  MOBILE_SECTION_TOP_OFFSET_RATIO,
  MOBILE_STICKY_NAVIGATION_MAX_WIDTH,
} from "@/hooks/useMobileStickyNavigation";
import { MobileSidebarContext } from "./mobileSidebarContext";

const MOBILE_STICKY_NAVIGATION_SECTION_SELECTOR =
  "[data-mobile-sticky-navigation-section]";

const isStickyNavigationSectionVisible = (section: HTMLElement): boolean => {
  const { bottom, top } = section.getBoundingClientRect();
  const viewportHeight = getMobileViewportHeight();

  return (
    top <= viewportHeight * MOBILE_SECTION_TOP_OFFSET_RATIO &&
    bottom > viewportHeight
  );
};

const useCompactMobileMenuButton = (): boolean => {
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const updateCompactState = (): void => {
      const isStickyNavigationVisible =
        window.innerWidth <= MOBILE_STICKY_NAVIGATION_MAX_WIDTH &&
        Array.from(
          document.querySelectorAll<HTMLElement>(
            MOBILE_STICKY_NAVIGATION_SECTION_SELECTOR,
          ),
        ).some(isStickyNavigationSectionVisible);

      setIsCompact((currentState) =>
        currentState === isStickyNavigationVisible
          ? currentState
          : isStickyNavigationVisible,
      );
    };

    const observer = new MutationObserver(updateCompactState);
    const visualViewport = window.visualViewport;

    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", updateCompactState);
    window.addEventListener("scroll", updateCompactState, { passive: true });
    visualViewport?.addEventListener("resize", updateCompactState);
    visualViewport?.addEventListener("scroll", updateCompactState);
    updateCompactState();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateCompactState);
      window.removeEventListener("scroll", updateCompactState);
      visualViewport?.removeEventListener("resize", updateCompactState);
      visualViewport?.removeEventListener("scroll", updateCompactState);
    };
  }, []);

  return isCompact;
};

export interface SidebarProps {
  children?: React.ReactNode;
  logo?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}

export const Sidebar = ({
  children,
  description,
  logo,
  className,
}: SidebarProps) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean | null>(null);
  const [isClient, setIsClient] = useState(false);
  const isMobileMenuButtonCompact = useCompactMobileMenuButton();

  // Wykrywanie, czy jesteśmy po stronie klienta
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Wykrywanie rozmiaru ekranu
  useEffect(() => {
    if (!isClient) return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1440);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [isClient]);

  // Blokowanie scrollowania treści w tle gdy menu otwarte na mobile
  useBodyScrollLock(Boolean(isMobileOpen && isMobile));

  // Blur głównej treści gdy menu otwarte na mobile
  useEffect(() => {
    if (isMobileOpen && isMobile) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }

    return () => {
      document.body.classList.remove("sidebar-open");
    };
  }, [isMobileOpen, isMobile]);

  // Zamykanie menu po kliknięciu w overlay
  const handleOverlayClick = () => {
    setIsMobileOpen(false);
  };

  // Zamknięcie menu przy nawigacji do sekcji (udostępniane przez kontekst)
  const closeMobileMenu = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  const mobileSidebarContext = useMemo(
    () => ({ closeMobileMenu }),
    [closeMobileMenu]
  );

  // Zamykanie menu po naciśnięciu Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isMobileOpen]);

  // Nie renderuj niczego podczas SSR i przed wykryciem typu urządzenia
  if (!isClient || isMobile === null) {
    return null;
  }

  return (
    <MobileSidebarContext.Provider value={mobileSidebarContext}>
      {/* Overlay - tylko na mobile gdy menu otwarte */}
      {isMobile && isMobileOpen && (
        <div
          className={clsx(
            "fixed inset-0 z-40 bg-black transition-opacity duration-300",
            isMobileOpen ? "opacity-50" : "opacity-0"
          )}
          onClick={handleOverlayClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          // Desktop styles - zawsze widoczny
          "bg-primary-200 fixed bottom-0 left-0 top-0 z-50 w-[300px] shadow-lg print:hidden",
          // Mobile styles - slide-in od lewej
          "desktop:translate-x-0 desktop:transition-none",
          {
            // Mobile: ukryty domyślnie, widoczny gdy otwarty
            "-translate-x-full transition-transform duration-300 ease-in-out":
              isMobile && !isMobileOpen,
            "translate-x-0 transition-transform duration-300 ease-in-out":
              isMobile && isMobileOpen,
            // Mobile: szerokość 60%
            "w-[80%] md:w-auto": isMobile,
          },
          className
        )}
      >
        <div className="relative h-full">
          <div className="h-full overflow-y-auto overscroll-y-contain">
            {/* Nagłówek sidebara: logo | separator | tytuł | X */}
            <div className="flex items-center border-b border-gray-200 bg-white px-4 py-3">
              {/* Logo */}
              {logo && (
                <div className="flex-1">
                  {logo}
                </div>
              )}
              {/* Separator */}
              {logo && (
                <div className="mx-4 h-10 w-px bg-gray-200 flex-shrink-0" />
              )}
              {/* Tytuł */}
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-extrabold uppercase leading-tight tracking-wide text-text-primary">
                  Raport<br />z ekspertyzy
                </p>
              </div>
              {/* Przycisk zamknięcia - tylko na mobile gdy sidebar otwarty */}
              {isMobile && isMobileOpen && (
                <button
                  onClick={() => setIsMobileOpen(false)}
                  className={clsx(
                    "ml-3 flex-shrink-0 flex items-center justify-center",
                    "h-10 w-10 rounded-full bg-[#20262A]",
                    "transition-colors duration-200",
                    "focus:ring-primary focus:outline-none focus:ring-2 focus:ring-offset-2", "absolute right-[-20px] top-4 z-10"
                  )}
                  aria-label="Zamknij menu"
                >
                  <span className="mb-0.5 text-[22px] font-bold leading-none text-white">
                    &times;
                  </span>
                </button>
              )}
            </div>
            {description && (
              <div className="px-4 pt-4 pb-4 mb-4 bg-white border-b border-primary-100">
                {description}
              </div>
            )}
            {children}
          </div>
        </div>
      </div>

      {/* Przycisk otwarcia menu - tylko na mobile gdy menu zamknięte */}
      {isMobile && !isMobileOpen && (
        <button
          onClick={() => setIsMobileOpen(true)}
          className={clsx(
            "flex fixed justify-center items-center z-[9999] print:hidden",
            "bg-white rounded-full shadow-2xl p-[10px]",
            isMobileMenuButtonCompact
              ? "right-3 bottom-3 h-16 w-16"
              : "right-6 bottom-6 h-[80px] w-[80px]",
            "transition-[width,height,right,bottom] duration-150 ease-out motion-reduce:transition-none",
            "focus:ring-primary focus:outline-none focus:ring-2 focus:ring-offset-2"
          )}
          aria-label="Otwórz menu"
          style={{ zIndex: 9999 }}
        >
          <div className="flex justify-center items-center w-full h-full rounded-full bg-gradient-primary">
            <svg
              width="28"
              height="24"
              viewBox="0 0 28 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M25.7895 19.7647C26.3572 19.765 26.903 19.9745 27.3138 20.3498C27.7247 20.725 27.9691 21.2374 27.9964 21.7806C28.0236 22.3238 27.8317 22.8562 27.4604 23.2676C27.089 23.6789 26.5667 23.9376 26.0017 23.9901L25.7895 24H2.21053C1.64284 23.9997 1.09702 23.7902 0.686167 23.415C0.275316 23.0397 0.0309296 22.5273 0.00364846 21.9841C-0.0236327 21.4409 0.168283 20.9085 0.539627 20.4971C0.910971 20.0858 1.43328 19.8271 1.99832 19.7746L2.21053 19.7647H25.7895ZM25.7895 9.88235C26.3757 9.88235 26.938 10.1055 27.3526 10.5026C27.7671 10.8997 28 11.4384 28 12C28 12.5616 27.7671 13.1003 27.3526 13.4974C26.938 13.8945 26.3757 14.1176 25.7895 14.1176H2.21053C1.62426 14.1176 1.062 13.8945 0.647448 13.4974C0.232894 13.1003 0 12.5616 0 12C0 11.4384 0.232894 10.8997 0.647448 10.5026C1.062 10.1055 1.62426 9.88235 2.21053 9.88235H25.7895ZM25.7895 0C26.3757 0 26.938 0.223109 27.3526 0.620245C27.7671 1.01738 28 1.55601 28 2.11765C28 2.67928 27.7671 3.21791 27.3526 3.61505C26.938 4.01219 26.3757 4.23529 25.7895 4.23529H2.21053C1.62426 4.23529 1.062 4.01219 0.647448 3.61505C0.232894 3.21791 0 2.67928 0 2.11765C0 1.55601 0.232894 1.01738 0.647448 0.620245C1.062 0.223109 1.62426 0 2.21053 0H25.7895Z"
                fill="white"
              />
            </svg>
          </div>
        </button>
      )}
    </MobileSidebarContext.Provider>
  );
};

export default Sidebar;
